/*
 * Free gift guard
 *
 * Keeps the free gifts of the cart within the allowance and gates every entry to
 * checkout rendered by the theme.
 *
 * The rule is rendered server side by snippets/freebie-allowance.liquid and is
 * mirrored here on the live cart (GET /cart.js), with the configuration printed by
 * layout/theme.liquid in <script type="application/json" id="freebie-config">:
 *   paid     sum of final_line_price of the lines that are neither free gifts
 *            (product id in settings.freebie_products) nor gift cards
 *   allowed  level 1 number when paid >= level 1 threshold, plus the level 2
 *            number when paid >= level 2 threshold; the thresholds are store
 *            currency units, converted with Shopify.currency.rate, in cents
 *   excess   gift pieces - allowed, never below 0
 * Gifts are identified by product id only, never by price: a gift may get a real
 * price that an automatic discount makes free.
 *
 * An excess is given back starting from the last gift line through
 * /cart/update.js (by line key), then shapes:modalcart:cartqtychange is dispatched
 * with { response, freebieGuard: true } so that cart-items.js, cart-footer.js and
 * cart-count.js morph the drawer and the cart page. The correction is silent.
 *
 * Runs on page load (unless the server rendered an empty cart), on back/forward
 * cache restores, when the tab becomes visible, after every cart event of the
 * theme and, awaited, before leaving for checkout.
 *
 * Leaving for checkout is never held up for long: every request is given up after
 * REQUEST_TIMEOUT and the check before checkout has a deadline, past which the
 * customer goes on to checkout as when the cart cannot be read.
 *
 * A storefront safeguard, not the enforcement: cart permalinks, the AJAX API and
 * express wallets reach checkout without running this file, so the rule must be
 * enforced by Shopify too (automatic discount or checkout validation).
 */
(() => {
  const configElement = document.getElementById('freebie-config');

  if (!configElement) return;

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (e) {
    return;
  }

  const giftProductIds = new Set((config.productIds || []).map(Number));

  // No gift configured: nothing to guard, checkout stays native
  if (giftProductIds.size === 0) return;

  const SECTIONS =
    config.sections || 'cart-items,cart-footer,cart-item-count,cart-live-region';
  const LOCK_KEY = 'is_adding';
  const LOCK_TIMEOUT = 3000;
  const MAX_RETRIES = 3;
  const RETRY_BASE_DELAY = 400;
  const REQUEST_TIMEOUT = 5000;
  const MAX_PASSES = 3;
  const PASSIVE_INTERVAL = 2000;
  const CHECKOUT_TIMEOUT = 5000;
  const CHECKOUT_MIN_CHECK = 1000;
  const CHECKOUT_CONTROL = 'input[name="checkout"], button[name="checkout"]';
  const CHECKOUT_NOTICE = '[data-freebie-checkout-notice]';
  const SERVER_DISABLED = '[data-freebie-checkout-disabled]';

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Checkout time limit, kept with timers: `reached` resolves and `expired` turns
  // true once ms have passed, and not before the deadline `after` if given
  const deadlineIn = (ms, after) => {
    const deadline = { expired: false };

    deadline.reached = Promise.all([wait(ms), after && after.reached]).then(
      () => {
        deadline.expired = true;
      }
    );

    return deadline;
  };

  /* Allowance, mirror of snippets/freebie-allowance.liquid ------------------ */

  const optionalNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const number = Number(value);

    return Number.isFinite(number) ? number : null;
  };

  const pieces = (value) => Math.max(parseInt(value, 10) || 0, 0);

  // Store currency units -> cents of the cart currency
  const thresholdCents = (units) => {
    const shopify = window.Shopify;
    const rate =
      Number(shopify && shopify.currency && shopify.currency.rate) || 1;

    return Math.round(units * rate * 100);
  };

  const isCart = (data) => !!data && Array.isArray(data.items);

  const allowanceOf = (cart) => {
    const giftLines = [];
    let paid = 0;
    let inside = 0;

    cart.items.forEach((item) => {
      if (giftProductIds.has(Number(item.product_id))) {
        giftLines.push(item);
        inside += pieces(item.quantity);
      } else if (!item.gift_card) {
        paid += Number(item.final_line_price) || 0;
      }
    });

    const level1 = optionalNumber(config.level1);
    const level2 = optionalNumber(config.level2);
    let allowed = 0;

    if (level1 !== null && paid >= thresholdCents(level1)) {
      allowed = pieces(config.level1Number);

      if (level2 !== null && paid >= thresholdCents(level2)) {
        allowed += pieces(config.level2Number);
      }
    }

    return {
      allowed,
      inside,
      paid,
      excess: Math.max(inside - allowed, 0),
      giftLines,
    };
  };

  /* Network ------------------------------------------------------------------ */

  // Shopify.routes.root keeps the market / language prefix (e.g. /en/)
  const rootUrl = () => {
    const shopify = window.Shopify;
    const themeRoutes = window.theme && window.theme.routes;
    const root =
      (shopify && shopify.routes && shopify.routes.root) ||
      (themeRoutes && themeRoutes.root_url) ||
      '/';

    return root.endsWith('/') ? root : `${root}/`;
  };

  const retryDelay = (retry, retryAfter) => {
    const seconds = parseFloat(retryAfter);

    if (seconds > 0) return Math.min(seconds * 1000, 5000);

    return RETRY_BASE_DELAY * 2 ** retry + Math.random() * 200;
  };

  const TIMED_OUT = {};

  // A single try, given up after REQUEST_TIMEOUT or when the checkout deadline is
  // reached: { ok, data } | { retry, retryAfter } | { fatal, status }
  const tryRequest = async (url, options, deadline) => {
    if (deadline && deadline.expired) return { retry: true };

    const controller =
      typeof AbortController === 'function' ? new AbortController() : null;
    let timer;

    const timeout = new Promise((resolve) => {
      timer = setTimeout(resolve, REQUEST_TIMEOUT);
    });
    const stopped = (
      deadline ? Promise.race([timeout, deadline.reached]) : timeout
    ).then(() => {
      if (controller) controller.abort();

      return TIMED_OUT;
    });
    // Without AbortController a stalled request is left behind, not waited for
    const settle = (promise) => Promise.race([promise, stopped]);

    try {
      let response;

      try {
        response = await settle(
          fetch(
            url,
            controller
              ? Object.assign({}, options, { signal: controller.signal })
              : options
          )
        );
      } catch (e) {
        return { retry: true };
      }

      if (response === TIMED_OUT) return { retry: true };

      if (response.status === 429 || response.status >= 500) {
        return { retry: true, retryAfter: response.headers.get('Retry-After') };
      }

      let data;

      try {
        data = await settle(response.json());
      } catch (e) {
        // A 200 that is not JSON is the bot protection page: worth another try
        return response.ok
          ? { retry: true }
          : { fatal: true, status: response.status };
      }

      if (data === TIMED_OUT) return { retry: true };

      return response.ok
        ? { ok: true, data }
        : { fatal: true, status: response.status };
    } finally {
      clearTimeout(timer);
    }
  };

  // Network errors, timeouts, 429 and 5xx are retried with an exponential
  // backoff, never past the checkout deadline
  const request = async (url, options, deadline) => {
    let result = await tryRequest(url, options, deadline);

    for (let retry = 0; result.retry && retry < MAX_RETRIES; retry++) {
      const delay = wait(retryDelay(retry, result.retryAfter));

      await (deadline ? Promise.race([delay, deadline.reached]) : delay);

      if (deadline && deadline.expired) break;

      result = await tryRequest(url, options, deadline);
    }

    return result;
  };

  const getCart = (deadline) =>
    request(
      `${rootUrl()}cart.js`,
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      },
      deadline
    );

  const updateCart = (body, deadline) =>
    request(
      `${rootUrl()}cart/update.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      },
      deadline
    );

  /* Add to cart mutex shared with assets/product-quick-buy.js ---------------- */

  let lockHeld = false;
  let lockLeftBehind = false;

  const lockIsFree = () => {
    try {
      const value = localStorage.getItem(LOCK_KEY);

      return value == null || value == 0;
    } catch (e) {
      return true;
    }
  };

  const setLock = (value) => {
    lockHeld = value === 1;

    try {
      localStorage.setItem(LOCK_KEY, value);
    } catch (e) {
      // localStorage unavailable (private mode): nothing to serialize with
    }
  };

  // Limited wait: a lock left behind by another tab must not block the guard.
  // Still held after LOCK_TIMEOUT, it is taken as left behind (page closed while
  // adding) and not waited for again until another tab changes it. Nothing waits
  // once the customer has asked for checkout.
  const waitForAddLock = async () => {
    const startedAt = Date.now();

    while (!lockIsFree()) {
      if (lockLeftBehind || checkoutInProgress) return;

      if (Date.now() - startedAt >= LOCK_TIMEOUT) {
        lockLeftBehind = true;
        return;
      }

      await wait(50);
    }

    lockLeftBehind = false;
  };

  window.addEventListener('storage', (event) => {
    if (event.key === LOCK_KEY) lockLeftBehind = false;
  });

  // Leaving the page during an update must not leave the lock behind
  window.addEventListener('pagehide', () => {
    if (lockHeld) setLock(0);
  });

  /* Theme cart listeners ----------------------------------------------------- */

  let alpineInitialized = false;
  let pendingCartChange = null;

  const alpineStarted = () => {
    if (alpineInitialized || !window.Alpine) return true;

    const component = document.querySelector('[x-data]');

    return !!(component && component._x_dataStack);
  };

  const dispatchCartChange = (detail) => {
    document.body.dispatchEvent(
      new CustomEvent('shapes:modalcart:cartqtychange', {
        bubbles: true,
        detail,
      })
    );
  };

  // cart-items.js, cart-footer.js and cart-count.js listen once Alpine has
  // started: a correction made before that is replayed on alpine:initialized
  const announceCorrection = (response) => {
    const detail = { response, freebieGuard: true };

    if (alpineStarted()) {
      dispatchCartChange(detail);
    } else {
      pendingCartChange = detail;
    }
  };

  /* Correction pass ---------------------------------------------------------- */

  // With a deadline (checkout) a pass neither waits for the add lock nor takes
  // it: a lock left behind must not delay checkout, and leaving for checkout must
  // not leave one behind. The update response is verified anyway.
  const correctExcess = async (deadline) => {
    for (let pass = 0; pass < MAX_PASSES; pass++) {
      if (deadline && deadline.expired) return { status: 'unknown' };

      if (!deadline) await waitForAddLock();

      const read = await getCart(deadline);

      // The cart cannot be read: Shopify's own rules have the last word
      if (!read.ok || !isCart(read.data)) return { status: 'unknown' };

      const state = allowanceOf(read.data);

      if (state.excess === 0) return { status: 'ok', state };

      // The last taken gifts are the first given back
      const updates = {};
      let excess = state.excess;

      for (let i = state.giftLines.length - 1; i >= 0 && excess > 0; i--) {
        const line = state.giftLines[i];
        const quantity = pieces(line.quantity);
        const givenBack = Math.min(quantity, excess);

        updates[line.key] = quantity - givenBack;
        excess -= givenBack;
      }

      if (!deadline) {
        await waitForAddLock();
        setLock(1);
      }

      let write;

      try {
        write = await updateCart(
          {
            updates,
            sections: SECTIONS,
            sections_url: window.location.pathname,
          },
          deadline
        );
      } finally {
        if (!deadline) setLock(0);
      }

      if (write.ok && isCart(write.data)) {
        if (write.data.sections) announceCorrection(write.data);

        const after = allowanceOf(write.data);

        if (after.excess === 0) {
          return { status: 'ok', state: after, corrected: true };
        }
      }
      // Otherwise the cart moved meanwhile or the update was refused: read again
    }

    // Cut by the checkout deadline, the excess is not known to be permanent
    return { status: deadline && deadline.expired ? 'unknown' : 'failed' };
  };

  /* Checkout controls -------------------------------------------------------- */

  const busyControls = new Set();
  let checkoutInProgress = false;

  // While a notice shows, the checkout control of its footer is described by it
  const showNotices = (visible, scope) => {
    (scope || document).querySelectorAll(CHECKOUT_NOTICE).forEach((notice) => {
      const footer = notice.closest('[data-cart-footer]');

      notice.classList.toggle('hidden', !visible);

      if (!footer) return;

      footer.querySelectorAll(CHECKOUT_CONTROL).forEach((control) => {
        if (visible && notice.id) {
          control.setAttribute('aria-describedby', notice.id);
        } else {
          control.removeAttribute('aria-describedby');
        }
      });
    });
  };

  const enableControl = (control) => {
    control.removeAttribute('aria-busy');
    control.removeAttribute('data-freebie-checkout-disabled');
    control.classList.remove('opacity-50', 'cursor-progress', 'cursor-not-allowed');

    if ('disabled' in control) {
      control.disabled = false;
      control.classList.add('cursor-pointer');
    }
  };

  const setBusy = (control) => {
    busyControls.add(control);
    control.setAttribute('aria-busy', 'true');
    control.classList.add('opacity-50', 'cursor-progress');

    if ('disabled' in control) control.disabled = true;
  };

  const releaseControls = () => {
    busyControls.forEach(enableControl);
    busyControls.clear();
  };

  // snippets/cart-footer.liquid renders the checkout button disabled, described
  // by its notice, when the server counts too many gifts. Once the live cart has
  // been checked the click interception below gates the button, so it is handed
  // back to the customer. The notice stays only for an excess that could not be
  // corrected: not for a corrected cart, nor for a cart that could not be read.
  const syncCheckoutControls = (result) => {
    showNotices(result.status === 'failed');

    document.querySelectorAll(SERVER_DISABLED).forEach((control) => {
      if (!busyControls.has(control)) enableControl(control);
    });
  };

  /* Single flight ------------------------------------------------------------ */

  let running = null;
  let queued = null;
  let queuedDeadline = null;
  let lastResult = null;
  let lastPassEndedAt = 0;

  const startPass = (deadline) => {
    const pass = correctExcess(deadline)
      .catch(() => ({ status: 'unknown' }))
      .then((result) => {
        lastResult = result;
        lastPassEndedAt = Date.now();

        try {
          syncCheckoutControls(result);
        } catch (e) {
          // The result stands even if the markup is not the expected one
        }

        return result;
      })
      .finally(() => {
        if (running === pass) running = null;
      });

    running = pass;

    return pass;
  };

  // One pass at a time. A call made while a pass runs waits for it and triggers
  // at most one more pass, which reads the cart as left by the running one and
  // keeps the deadline of a checkout waiting for it.
  // Passive triggers (load, tab visible) only join a running or very recent pass.
  const enforce = ({ passive = false, deadline = null } = {}) => {
    if (running && passive) return running;

    if (!running) {
      if (
        passive &&
        lastResult &&
        Date.now() - lastPassEndedAt < PASSIVE_INTERVAL
      ) {
        return Promise.resolve(lastResult);
      }

      return startPass(deadline);
    }

    if (deadline) queuedDeadline = deadline;

    if (!queued) {
      queued = running.then(() => {
        const passDeadline = queuedDeadline;

        queued = null;
        queuedDeadline = null;

        return running || startPass(passDeadline);
      });
    }

    return queued;
  };

  // Footers rendered after the last pass ended (the drawer teleported when Alpine
  // starts, a footer morphed on a cart event) get its outcome too
  const syncRenderedControls = () => {
    if (!lastResult || running) return;

    try {
      syncCheckoutControls(lastResult);
    } catch (e) {
      // The result stands even if the markup is not the expected one
    }
  };

  /* Checkout ----------------------------------------------------------------- */

  // What the native form post would have sent and is not in the cart yet: the
  // quantities typed in the cart (values differing from data-last-value, gift
  // lines excluded: the allowance check takes care of them) and the note.
  const flushPendingChanges = async (control, deadline) => {
    const ownForm = control ? control.form : null;
    const ownFooter = control ? control.closest('[data-cart-footer]') : null;
    const updates = {};
    let hasUpdates = false;

    // Drawer and cart page may both be in the page: the fields of the form the
    // clicked control submits come last, so they win
    const fields = Array.from(
      document.querySelectorAll('input[name^="updates["]')
    ).sort((a, b) => (a.form === ownForm) - (b.form === ownForm));

    fields.forEach((field) => {
      if (
        field.hasAttribute('data-freebie-line') ||
        field.readOnly ||
        field.disabled
      ) {
        return;
      }

      const key = field.name.slice('updates['.length, -1);
      const value = field.value.trim();

      if (!key || !/^\d+$/.test(value)) return;

      const lastValue = field.getAttribute('data-last-value');

      if (lastValue !== null && parseInt(lastValue, 10) === parseInt(value, 10)) {
        return;
      }

      updates[key] = parseInt(value, 10);
      hasUpdates = true;
    });

    const notes = Array.from(
      document.querySelectorAll('textarea[name="note"]')
    ).filter((field) => field.value !== field.defaultValue);
    const note =
      notes.find((field) => ownFooter && ownFooter.contains(field)) || notes[0];

    if (!hasUpdates && !note) return;

    const body = {};

    if (hasUpdates) body.updates = updates;
    if (note) body.note = note.value;

    // Not awaited for its outcome: the allowance check reads the cart anyway
    await updateCart(body, deadline);
  };

  const cartPageUrl = () => {
    const themeRoutes = window.theme && window.theme.routes;

    return (themeRoutes && themeRoutes.cart_url) || `${rootUrl()}cart`;
  };

  const checkoutUrl = (link) => {
    let url = `${rootUrl()}checkout`;

    if (link) {
      try {
        url += new URL(link.href, window.location.href).search;
      } catch (e) {
        // Plain checkout url
      }
    }

    return url;
  };

  const goToCheckout = async (control, link) => {
    const trigger = control || link;

    if (checkoutInProgress) return;

    checkoutInProgress = true;
    setBusy(trigger);

    // Never held up for long: the flush and the allowance check share
    // CHECKOUT_TIMEOUT, and after a slow flush the check still gets
    // CHECKOUT_MIN_CHECK. Past the deadline the customer goes on to checkout.
    const budget = deadlineIn(CHECKOUT_TIMEOUT);
    let result;

    try {
      await flushPendingChanges(control, budget);

      const deadline = deadlineIn(CHECKOUT_MIN_CHECK, budget);

      result = await Promise.race([
        enforce({ deadline }),
        deadline.reached.then(() => ({ status: 'unknown' })),
      ]);
    } catch (e) {
      result = { status: 'unknown' };
    }

    if (result.status === 'failed') {
      checkoutInProgress = false;
      releaseControls();

      const footer = trigger.closest('[data-cart-footer]');

      if (footer && footer.querySelector(CHECKOUT_NOTICE)) {
        showNotices(true, footer);
      } else {
        // No notice next to this control: the cart page renders it
        window.location.assign(cartPageUrl());
      }

      return;
    }

    // Allowance respected, cart unreadable or deadline reached: checkout, where
    // Shopify's own rules apply. Never the native form post: the updates[] fields
    // in the page may still hold the old quantities and would bring the excess back.
    window.location.assign(checkoutUrl(link));

    // Navigation cancelled or blocked: give the control back after a while
    setTimeout(() => {
      checkoutInProgress = false;
      releaseControls();
    }, 10000);
  };

  // Only the checkout route itself: /checkout or Shopify.routes.root + 'checkout',
  // same origin, with or without query string and hash
  const checkoutLinkOf = (element) => {
    const link = element.closest('a[href]');

    if (!link) return null;

    try {
      const url = new URL(link.href, window.location.href);
      const path = url.pathname.replace(/\/$/, '');

      if (
        url.origin === window.location.origin &&
        (path === '/checkout' || path === `${rootUrl()}checkout`)
      ) {
        return link;
      }
    } catch (e) {
      // Not a valid url
    }

    return null;
  };

  // Capture phase: runs before the native submit or navigation of the control
  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      const control = event.target.closest(CHECKOUT_CONTROL);
      const link = control ? null : checkoutLinkOf(event.target);

      if (!control && !link) return;

      event.preventDefault();
      goToCheckout(control, link);
    },
    true
  );

  // requestSubmit() or the Enter key with a checkout control as submitter
  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;

      if (!(submitter instanceof Element) || !submitter.matches(CHECKOUT_CONTROL)) {
        return;
      }

      event.preventDefault();
      goToCheckout(submitter, null);
    },
    true
  );

  /* Triggers ----------------------------------------------------------------- */

  const onCartEvent = (event) => {
    // cart-footer.js morphs the footers in listeners added after this one
    setTimeout(syncRenderedControls, 0);

    if (event.detail && event.detail.freebieGuard) return;

    enforce();
  };

  // A cart rendered empty by the server has nothing to correct on page load
  const enforceOnLoad = () => {
    if (config.itemCount !== 0) enforce({ passive: true });
  };

  window.addEventListener('shapes:modalcart:afteradditem', onCartEvent);
  window.addEventListener('shapes:modalcart:cartqtychange', onCartEvent);

  document.addEventListener('alpine:initialized', () => {
    alpineInitialized = true;

    if (pendingCartChange) {
      const detail = pendingCartChange;

      pendingCartChange = null;
      dispatchCartChange(detail);
    }

    // The drawer footer has just been teleported, maybe after the first pass
    syncRenderedControls();
    enforceOnLoad();
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) {
      enforceOnLoad();
      return;
    }

    // Restored from the back/forward cache (e.g. back from checkout): the cart may
    // have changed and a checkout control may have been left busy
    checkoutInProgress = false;
    releaseControls();
    enforce();
  });

  // Another tab may have changed the cart
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') enforce({ passive: true });
  });

  // First page view after a permalink or an AJAX API change
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceOnLoad);
  } else {
    enforceOnLoad();
  }

  // Manual check from the browser console: await FreebieGuard.enforce()
  window.FreebieGuard = { enforce: () => enforce() };
})();
